import { ChangeEvent, KeyboardEvent, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon, TextInput } from "@leagueplatform/genesis-core";
import {
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
} from "@leagueplatform/masonry-engine";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";

const SEARCH_PARAM_KEY = "searchParam";

/**
 * Renderer for the BE `careLibrarySearchField` node, mounted at the top of
 * `careLibrary$root` (the masonry tree behind `/member/health-programs`) and
 * at the top of `careLibrary$search` (the results page). Mirrors the iOS and
 * Android renderers so the Web Journey Explore tab shows the same search bar.
 *
 * Submit behaviour navigates to the search results route with the query as a
 * `searchParam` URL parameter. The BE node's data mapping reads
 * `request.queryParameters.#(key=="searchParam").value` so the parameter key
 * must match exactly.
 */
type MasonryCareLibrarySearchFieldProperties = {
  placeholder?: string;
  text?: string;
};

type MasonryCareLibrarySearchFieldNode = MasonryEngineNode<
  "careLibrarySearchField",
  MasonryCareLibrarySearchFieldProperties
>;

type MasonryCareLibrarySearchFieldRendererProps =
  MasonryEngineNodeRendererProps<MasonryCareLibrarySearchFieldNode>;

export const MasonryCareLibrarySearchFieldRenderer = ({
  placeholder,
  text = "",
}: MasonryCareLibrarySearchFieldRendererProps) => {
  const [value, setValue] = useState(text);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSubmit = () => {
    const query = value.trim();
    if (!query) return;
    navigate(
      `${
        APP_MANAGED_ROUTES.CARE_LIBRARY_SEARCH
      }?${SEARCH_PARAM_KEY}=${encodeURIComponent(query)}`,
      { replace: pathname === APP_MANAGED_ROUTES.CARE_LIBRARY_SEARCH }
    );
  };

  return (
    <TextInput
      placeholder={placeholder}
      value={value}
      leadingContent={<Icon icon="interfaceSearch" size="$one" />}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        setValue(event.target.value)
      }
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") handleSubmit();
      }}
    />
  );
};
